import { zeroAddress } from 'viem'
import { exactlyConfig } from '@1delta/data-sdk'
import { ExactlyAuditorAbi, ExactlyPreviewerAbi } from '@1delta/abis'
import { getEvmClient } from '@1delta/providers'
import type { ExactlyMarketAccount, ExactlyMarketsRaw } from './types'

/**
 * Fetch all Exactly market data for a chain — FULLY ON-CHAIN (no API, no
 * indexer): a single `Previewer.exactly(address(0))` eth_call returns every
 * market with its fixed pools (maturities + rates + liquidity), floating side,
 * prices and risk params; a second call reads the Auditor liquidation bonus.
 * The account-scoped fields are zero for the zero address and ignored.
 *
 * Returns `{ markets: [], liquidationIncentive: null }` when the chain has no
 * Exactly config or the read fails (the converter then emits nothing).
 */
export async function fetchExactlyMarkets(
  chainId: string,
): Promise<ExactlyMarketsRaw> {
  const cfg = exactlyConfig()?.[chainId]
  if (!cfg?.previewer || !cfg?.auditor) {
    return { markets: [], liquidationIncentive: null }
  }

  const client = getEvmClient(chainId)

  const [markets, liquidationIncentive] = await Promise.all([
    client
      .readContract({
        address: cfg.previewer as any,
        abi: ExactlyPreviewerAbi,
        functionName: 'exactly',
        args: [zeroAddress],
      })
      .then((res: any) => res as ExactlyMarketAccount[])
      .catch(() => [] as ExactlyMarketAccount[]),
    client
      .readContract({
        address: cfg.auditor as any,
        abi: ExactlyAuditorAbi,
        functionName: 'liquidationIncentive',
      })
      .then((res: any) => ({
        liquidator: res[0] as bigint,
        lenders: res[1] as bigint,
      }))
      .catch(() => null),
  ])

  return { markets: [...markets], liquidationIncentive }
}
